import { moonbase } from "@/lib/moonbase";
import { useEffect, useRef, useState } from "react";
import invariant from "@moonbaseai/invariant";

function useCaptchaLoader() {
  const captchaRef = useRef<HTMLDivElement>(null);
  const captchaTimeout = useRef<number | undefined>(undefined);
  const captchaLoadAttempts = useRef(0);
  const captchLoadMaxAttempts = 10;
  const captchaLoadMaxDelay = 1000;
  const [captchaWidgetId, setCaptchaWidgetId] = useState<string | null>(null);

  useEffect(() => {
    const load = () => {
      const container = captchaRef.current;
      invariant(container !== null, "captcha container is required");

      const siteKey = moonbase.config.hcaptchaSiteKey;

      const widgetId = moonbase.captcha.renderCaptcha({
        container,
        siteKey,
      });

      setCaptchaWidgetId(widgetId);
    };

    const attempt = () => {
      try {
        captchaLoadAttempts.current += 1;
        load();
      } catch (error) {
        if (captchaLoadAttempts.current > captchLoadMaxAttempts) {
          console.error("captcha: max attempts reached", error);
          return;
        }

        const delay = Math.min(
          captchaLoadMaxDelay,
          Math.pow(5, captchaLoadAttempts.current) + Math.round(Math.random() * 10),
        );

        console.warn("captcha: retrying in", delay, "ms");
        window.clearTimeout(captchaTimeout.current);
        captchaTimeout.current = window.setTimeout(attempt, delay);
      }
    };

    window.clearTimeout(captchaTimeout.current);
    captchaTimeout.current = window.setTimeout(attempt, 1);

    return () => {
      window.clearTimeout(captchaTimeout.current);
    };
  }, []);

  return { captchaRef, captchaWidgetId } as const;
}

export { useCaptchaLoader };
